import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import type { ErrorLike } from '@apollo/client';
import { isErrorLike } from '@apollo/client/errors';
import { LoadingButton, type LoadingButtonProps } from '@mui/lab';
import {
  Alert,
  Box,
  type BoxProps,
  Button,
  type ButtonProps,
  Grid,
  type GridProps,
  Paper,
  type PaperProps,
  Snackbar,
  Typography,
} from '@mui/material';
import { Save } from 'lucide-react';
import {
  Controller,
  type DeepPartial,
  type FieldPath,
  type FieldValue,
  FormProvider,
  type Path,
  type PathValue,
  type Primitive,
  type UseFormReturn,
  type UseFormSetError,
  useForm,
} from 'react-hook-form';

import {
  type Leads_Bool_Exp,
  type Leads_Order_By,
  type Lots_Bool_Exp,
} from '@graphql/__generated__/graphql';
import type { AutocompleteMultiProps } from '@modules/_shared/components/AutocompleteMulti';
import type { RaAutoCompleteCreatableItem } from '@modules/_shared/components/data-grid/RaAutoComplete';
import type { SuggestionsType } from '@modules/_shared/components/email-editor';
import {
  RaAddressInput,
  type RaAddressInputProps,
} from '@modules/_shared/components/form/RaAddressInput';
import { RaArrayInput } from '@modules/_shared/components/form/RaArrayInput';
import { RaCheckbox } from '@modules/_shared/components/form/RaCheckbox';
import { RaCheckboxGroup } from '@modules/_shared/components/form/RaCheckboxGroup';
import { RaColorPicker } from '@modules/_shared/components/form/RaColorPicker';
import { RaDate } from '@modules/_shared/components/form/RaDate';
import type { RaDictionaryProps } from '@modules/_shared/components/form/RaDictionary';
import { RaDictionary } from '@modules/_shared/components/form/RaDictionary';
import {
  type ImageObjectFit,
  type ImageUploadResult,
  RaImageUpload,
} from '@modules/_shared/components/form/RaImageUpload';
import { RaLead } from '@modules/_shared/components/form/RaLead';
import { RaLot } from '@modules/_shared/components/form/RaLot';
import { RaNumber } from '@modules/_shared/components/form/RaNumber';
import { RaOrganisation } from '@modules/_shared/components/form/RaOrganisation';
import { RaPhoneNumber } from '@modules/_shared/components/form/RaPhoneNumber';
import {
  RaRange,
  type RaRangeProps,
} from '@modules/_shared/components/form/RaRange';
import { RaRichEditorSuggestions } from '@modules/_shared/components/form/RaRichEditorSuggestions';
import {
  RaSelect,
  type RaSelectOption,
} from '@modules/_shared/components/form/RaSelect';
import { RaTags } from '@modules/_shared/components/form/RaTags';
import {
  RaTeam,
  type RaTeamProps,
} from '@modules/_shared/components/form/RaTeam';
import { RaTextField } from '@modules/_shared/components/form/RaTextfield';
import { RaTime } from '@modules/_shared/components/form/RaTime';
import {
  RaUser,
  type RaUserProps,
} from '@modules/_shared/components/form/RaUser';
import { MarkdownComposer } from '@modules/_shared/components/MarkdownComposer';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { getKeys, isDeepEqual } from '@modules/_shared/utils/objects';
import {
  type IFormError,
  extractMessageFromError,
} from '@modules/_shared/utils/parseError';
import { type Translate, useLocale } from '@shared/hooks/locale';

export interface BaseFieldDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> {
  type:
    | 'text'
    | 'date'
    | 'time'
    | 'select'
    | 'checkbox'
    | 'checkbox-group'
    | 'category-title'
    | 'address'
    | 'organisation'
    | 'team'
    | 'lot'
    | 'color-picker'
    | 'lead'
    | 'user'
    | 'number'
    | 'range'
    | 'dictionary'
    | 'rich-text'
    | 'custom'
    | 'phone_number'
    | 'tags'
    | 'rich-editor-suggestions'
    | 'image'
    | 'array'
    | 'group';
  name: FieldPath<TFormData>;
  label?: string;
  required?: boolean;
  disabled?: (data: TFormData) => boolean;
  gridProps?: FormItemGridSize;
  render?: (data: TFormData) => boolean;
  helpText?: string | ((data: TFormData) => string);
  tooltip?: string | ((data: TFormData) => string);
}

interface BaseSelectElementDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends BaseFieldDefinition<TFormData> {
  type: 'select';
  options: (data: TFormData) => RaSelectOption[];
  label: string;
}

interface DropdownElementDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends BaseSelectElementDefinition<TFormData> {
  multiple?: boolean;
  renderValue?: (value: any) => React.ReactNode;
  renderOption?: (option: RaSelectOption) => React.ReactNode;
  emptyValueAsNull?: boolean;
  clearable?: boolean;
}

interface ToggleButtonsElementDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends BaseSelectElementDefinition<TFormData> {
  toggleButtons: true;
}

export type SelectFieldDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> =
  | DropdownElementDefinition<TFormData>
  | ToggleButtonsElementDefinition<TFormData>;

export interface TextFieldDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends BaseFieldDefinition<TFormData> {
  type: 'text';
  inputType?: React.HTMLInputTypeAttribute;
  multiline?: boolean;
  rows?: number;
  suffix?: string;
  prefix?: string;
  label: string;
  actions?:
    | React.ReactNode
    | ((
        formMethods: Pick<
          UseFormReturn<TFormData>,
          'setValue' | 'getValues' | 'reset'
        >,
      ) => React.ReactNode);
}

export interface NumberFieldDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends BaseFieldDefinition<TFormData> {
  type: 'number';
  min?: number;
  max?: number;
  decimalNumbers?: number;
  suffix?: string;
  prefix?: string;
  disableFormatting?: boolean;
  label: string;
}

export interface RangeFieldDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends BaseFieldDefinition<TFormData>,
    Pick<RaRangeProps, 'slotProps'> {
  type: 'range';
  label: string;
}

export interface PhoneFieldDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends BaseFieldDefinition<TFormData> {
  type: 'phone_number';
  label: string;
}

export interface DatePickerDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends BaseFieldDefinition<TFormData> {
  type: 'date';
  label: string;
}

export interface TimeFieldDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends BaseFieldDefinition<TFormData> {
  type: 'time';
  label: string;
}

export interface CheckboxFieldDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends BaseFieldDefinition<TFormData> {
  type: 'checkbox';
  style?: 'switch' | 'checkbox';
  label: string;
}

export interface CheckboxGroupFieldDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends Omit<BaseFieldDefinition<TFormData>, 'name'> {
  name: string;
  type: 'checkbox-group';
  checkboxes: CheckboxFieldDefinition<TFormData>[];
  label: string;
}

export interface ColorPickerFieldDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends BaseFieldDefinition<TFormData> {
  type: 'color-picker';
  label: string;
}

export interface CategoryTitleDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends Omit<BaseFieldDefinition<TFormData>, 'name'> {
  name: string;
  type: 'category-title';
  label: string;
}

export interface GroupFieldDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends Omit<
    BaseFieldDefinition<TFormData>,
    'name' | 'required' | 'disabled'
  > {
  name: string;
  type: 'group';
  fields: FormFieldDefinitionType<TFormData>[];
  label?: never;
}

export type AddressFieldDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> = Omit<BaseFieldDefinition<TFormData>, 'name'> &
  Pick<RaAddressInputProps, 'includeGoogleFields' | 'countryRestriction'> & {
    type: 'address';
    path?: FieldPath<TFormData>;
    requiredFields?: (data: TFormData) => {
      street_number?: boolean;
      country_code?: boolean;
      country?: boolean;
      locality?: boolean;
      postcode?: boolean;
      state?: boolean;
      route?: boolean;
    };
  };

export interface OrganisationFieldDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends BaseFieldDefinition<TFormData> {
  type: 'organisation';
  label: string;
}

export interface LotFieldDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends BaseFieldDefinition<TFormData> {
  type: 'lot';
  label: string;
  filters?: Lots_Bool_Exp;
  limitToDevelopment?: string;
}

export interface LeadFieldDefinitionBase<
  TFormData extends Record<string, any> = Record<string, any>,
> extends BaseFieldDefinition<TFormData> {
  type: 'lead';
  label: string;
  placeholder?: string;
  autoFocus?: boolean;
  customWhereClause?: (searchValue?: string) => Leads_Bool_Exp;
  orderBy?: Leads_Order_By;
}

export type LeadFieldDefinitionWithTsv<
  TFormData extends Record<string, any> = Record<string, any>,
> = LeadFieldDefinitionBase<TFormData> & {
  useSearchTsv: true;
  instantQuery?: never;
};

export type LeadFieldDefinitionWithoutTsv<
  TFormData extends Record<string, any> = Record<string, any>,
> = LeadFieldDefinitionBase<TFormData> & {
  useSearchTsv?: false;
  instantQuery?: boolean;
};

export type LeadFieldDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> =
  | LeadFieldDefinitionWithTsv<TFormData>
  | LeadFieldDefinitionWithoutTsv<TFormData>;

export interface UserFieldDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends BaseFieldDefinition<TFormData>,
    Pick<RaUserProps, 'createUserSelected' | 'filters' | 'multiple'> {
  type: 'user';
  label: string;
  autoFocus?: boolean;
  /** Team field (string-valued) whose selected team's brokers are listed on click. */
  shortlistTeamField?: {
    [K in FieldPath<TFormData>]: PathValue<TFormData, K> extends
      | string
      | null
      | undefined
      ? K
      : never;
  }[FieldPath<TFormData>];
}

export interface TeamFieldDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends BaseFieldDefinition<TFormData>,
    Pick<RaTeamProps, 'multiple'> {
  type: 'team';
  label: string;
}

export interface DictionaryFieldDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends BaseFieldDefinition<TFormData> {
  type: 'dictionary';
  dictionaryType: RaDictionaryProps['dictionaryType'];
  multiple?: boolean;
  label: string;
  valueField?: 'id' | 'name';
}

export interface RichTextFieldDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends BaseFieldDefinition<TFormData> {
  type: 'rich-text';
  label: string;
  resetKey?: number;
  preserveLineBreaks?: boolean;
  format?: 'markdown' | 'html';
  minHeight?: string;
  maxHeight?: string;
  actions?:
    | React.ReactNode
    | ((
        formMethods: Pick<
          UseFormReturn<TFormData>,
          'setValue' | 'getValues' | 'reset'
        >,
      ) => React.ReactNode);
}

export interface RichEditorSuggestionsFieldDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends BaseFieldDefinition<TFormData> {
  type: 'rich-editor-suggestions';
  label: string;
  singleLine?: boolean;
  suggestions?: SuggestionsType;
  resetKey?: string;
  snippetLotId?: string;
  snippetDevelopmentId?: string;
  onImageUpload?: (file: File) => Promise<string | null>;
}

export interface CustomElementDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends Omit<BaseFieldDefinition<TFormData>, 'name'> {
  name: string;
  type: 'custom';
  element: React.ReactNode | ((data: TFormData) => React.ReactNode);
  label?: never;
}

interface BaseTagsElementDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
  TFieldValue extends FieldValue<TFormData> = any,
> extends BaseFieldDefinition<TFormData>,
    Pick<
      AutocompleteMultiProps<TFieldValue>,
      | 'InputProps'
      | 'getOptionLabel'
      | 'isOptionEqualToValue'
      | 'renderTags'
      | 'groupBy'
    > {
  type: 'tags';
  onFilter: (inputStr: string) => Promise<TFieldValue[]> | TFieldValue[];
  label: string;
}

interface CreatableTagsElementDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
  TFieldValue extends FieldValue<TFormData> = any,
> extends BaseTagsElementDefinition<TFormData, TFieldValue> {
  onNewTagSelected: (tag: string) => Promise<TFieldValue> | TFieldValue;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: TFieldValue | RaAutoCompleteCreatableItem<TFieldValue>,
  ) => React.ReactNode;
}

interface NonCreatableTagsElementDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
  TFieldValue extends FieldValue<TFormData> = any,
> extends BaseTagsElementDefinition<TFormData, TFieldValue> {
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: TFieldValue,
  ) => React.ReactNode;
}

export type TagsElementDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
  TFieldValue extends FieldValue<TFormData> = any,
> =
  | CreatableTagsElementDefinition<TFormData, TFieldValue>
  | NonCreatableTagsElementDefinition<TFormData, TFieldValue>;

export interface ImageElementDefinition<
  TFormData extends Record<string, any> = Record<string, any>,
> extends BaseFieldDefinition<TFormData> {
  type: 'image';
  imageObjectFit?: ImageObjectFit;
  onFileUpload: (file: ImageUploadResult) => Promise<string | null>;
}

export interface ArrayFieldDefinition<TFormData extends Record<string, any>>
  extends BaseFieldDefinition<TFormData> {
  type: 'array';
  label: string;
}

export type FormItemGridSize = Partial<
  Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', number | 'auto' | 'grow'>
> & {
  alignSelf?: string;
};

const defaultGridProps: FormItemGridSize = {
  xs: 12,
  md: 6,
};

const normalizeRenderOrOpts = <TFormData extends Record<string, any>>(
  renderOrOpts:
    | ((data: TFormData) => boolean)
    | BaseElementOptions<TFormData>
    | undefined,
): BaseElementOptions<TFormData> | undefined =>
  typeof renderOrOpts === 'function' ? { render: renderOrOpts } : renderOrOpts;

type FormHelpers<TFormData extends Record<string, any>> = Pick<
  UseFormReturn<TFormData>,
  'setValue' | 'getValues' | 'reset'
>;

const resolveFieldActions = <TFormData extends Record<string, any>>(
  actions:
    | React.ReactNode
    | ((formMethods: FormHelpers<TFormData>) => React.ReactNode)
    | undefined,
  formHelpers: FormHelpers<TFormData>,
): React.ReactNode =>
  typeof actions === 'function' ? actions(formHelpers) : actions;

const resolveItemSize = (
  props?: FormItemGridSize,
  { includeDefaults = true }: { includeDefaults?: boolean } = {},
):
  | Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', number | 'auto' | 'grow'>>
  | undefined => {
  const { alignSelf: _alignSelf, ...size } = props ?? {};
  if (!includeDefaults) {
    return props == null ? undefined : size;
  }
  return { ...defaultGridProps, ...size };
};

const resolveItemSx = (props?: FormItemGridSize) =>
  props?.alignSelf ? { alignSelf: props.alignSelf } : undefined;

export const defaultFormElement: Pick<
  BaseFieldDefinition,
  'type' | 'gridProps' | 'render'
> = {
  type: 'text',
  gridProps: {
    ...defaultGridProps,
  },
  render: () => true,
};

type BaseElementOptions<
  TFormData extends Record<string, any> = Record<string, any>,
> = Pick<
  BaseFieldDefinition<TFormData>,
  'render' | 'required' | 'disabled' | 'gridProps' | 'helpText'
>;

type CreateElementParams<
  TFormData extends Record<string, any> = Record<string, any>,
  TExtraOptions extends object = {},
> = [
  name: FieldPath<TFormData>,
  label: string,
  options?: BaseElementOptions<TFormData> & TExtraOptions,
];

const createBaseElement = <
  TFormData extends Record<string, any> = Record<string, any>,
>(
  ...args: CreateElementParams<TFormData>
): BaseFieldDefinition<TFormData> & { label: string } => {
  const [name, label, options] = args;

  return {
    ...defaultFormElement,
    name,
    label,
    ...(options ?? {}),
  };
};

function createTextFieldElementFn<
  TFormData extends Record<string, any> = Record<string, any>,
>(
  name: FieldPath<TFormData>,
  label: string,
  render?: (data: TFormData) => boolean,
): TextFieldDefinition<TFormData>;
function createTextFieldElementFn<
  TFormData extends Record<string, any> = Record<string, any>,
>(
  ...args: CreateElementParams<
    TFormData,
    Pick<TextFieldDefinition, 'multiline' | 'suffix' | 'prefix'>
  >
): TextFieldDefinition<TFormData>;
function createTextFieldElementFn<
  TFormData extends Record<string, any> = Record<string, any>,
>(
  name: FieldPath<TFormData>,
  label: string,
  renderOrOpts?:
    | ((data: TFormData) => boolean)
    | (BaseElementOptions<TFormData> &
        Pick<TextFieldDefinition, 'multiline' | 'suffix' | 'prefix'>),
): TextFieldDefinition<TFormData> {
  return {
    ...createBaseElement(name, label, normalizeRenderOrOpts(renderOrOpts)),
    type: 'text',
  };
}

export const createTextFieldElement = createTextFieldElementFn;

function createNumberFieldElementFn<
  TFormData extends Record<string, any> = Record<string, any>,
>(
  name: FieldPath<TFormData>,
  label: string,
  render?: (data: TFormData) => boolean,
): NumberFieldDefinition<TFormData>;
function createNumberFieldElementFn<
  TFormData extends Record<string, any> = Record<string, any>,
>(
  ...args: CreateElementParams<
    TFormData,
    Pick<
      NumberFieldDefinition,
      | 'min'
      | 'max'
      | 'decimalNumbers'
      | 'suffix'
      | 'prefix'
      | 'disableFormatting'
    >
  >
): NumberFieldDefinition<TFormData>;
function createNumberFieldElementFn<
  TFormData extends Record<string, any> = Record<string, any>,
>(
  name: FieldPath<TFormData>,
  label: string,
  renderOrOpts?:
    | ((data: TFormData) => boolean)
    | (BaseElementOptions<TFormData> &
        Pick<
          NumberFieldDefinition,
          | 'min'
          | 'max'
          | 'decimalNumbers'
          | 'suffix'
          | 'prefix'
          | 'disableFormatting'
        >),
): NumberFieldDefinition<TFormData> {
  return {
    ...createBaseElement(name, label, normalizeRenderOrOpts(renderOrOpts)),
    type: 'number',
  };
}

export const createNumberFieldElement = createNumberFieldElementFn;

function createSelectElementFn<
  TFormData extends Record<string, any> = Record<string, any>,
>(
  name: FieldPath<TFormData>,
  label: string,
  selectOptions: SelectFieldDefinition<TFormData>['options'],
  render?: (data: TFormData) => boolean,
): SelectFieldDefinition<TFormData>;

function createSelectElementFn<
  TFormData extends Record<string, any> = Record<string, any>,
>(
  name: FieldPath<TFormData>,
  label: string,
  selectOptions: SelectFieldDefinition<TFormData>['options'],
  options?: BaseElementOptions<TFormData>,
  multiple?: boolean,
  renderValue?: (value: any) => React.ReactNode,
): SelectFieldDefinition<TFormData>;

function createSelectElementFn<
  TFormData extends Record<string, any> = Record<string, any>,
>(
  name: FieldPath<TFormData>,
  label: string,
  selectOptions: SelectFieldDefinition['options'],
  renderOrOpts?: ((data: TFormData) => boolean) | BaseElementOptions<TFormData>,
  multiple = false,
  renderValue?: (value: any) => React.ReactNode,
): SelectFieldDefinition<TFormData> {
  return {
    ...createBaseElement(
      name,
      label,
      typeof renderOrOpts === 'function'
        ? { render: renderOrOpts }
        : renderOrOpts,
    ),
    type: 'select',
    options: selectOptions,
    multiple,
    renderValue,
  };
}

export const createSelectElement = createSelectElementFn;

function createCheckboxElementFn<
  TFormData extends Record<string, any> = Record<string, any>,
>(
  name: FieldPath<TFormData>,
  label: string,
  render?: (data: TFormData) => boolean,
): CheckboxFieldDefinition<TFormData>;
function createCheckboxElementFn<
  TFormData extends Record<string, any> = Record<string, any>,
>(
  ...args: CreateElementParams<
    TFormData,
    Pick<CheckboxFieldDefinition, 'style'>
  >
): CheckboxFieldDefinition<TFormData>;
function createCheckboxElementFn<
  TFormData extends Record<string, any> = Record<string, any>,
>(
  name: FieldPath<TFormData>,
  label: string,
  renderOrOpts?:
    | ((data: TFormData) => boolean)
    | (BaseElementOptions<TFormData> & Pick<CheckboxFieldDefinition, 'style'>),
): CheckboxFieldDefinition<TFormData> {
  return {
    ...createBaseElement(name, label, normalizeRenderOrOpts(renderOrOpts)),
    type: 'checkbox',
  };
}

export const createCheckboxElement = createCheckboxElementFn;

function createCheckboxGroupElementFn<
  TFormData extends Record<string, any> = Record<string, any>,
>(
  name: string,
  label: string,
  checkboxes: CheckboxGroupFieldDefinition<TFormData>['checkboxes'],
  render?: (data: TFormData) => boolean,
): CheckboxGroupFieldDefinition<TFormData>;
function createCheckboxGroupElementFn<
  TFormData extends Record<string, any> = Record<string, any>,
>(
  name: string,
  label: string,
  checkboxes: CheckboxGroupFieldDefinition<TFormData>['checkboxes'],
  options?: BaseElementOptions<TFormData>,
): CheckboxGroupFieldDefinition<TFormData>;
function createCheckboxGroupElementFn<
  TFormData extends Record<string, any> = Record<string, any>,
>(
  name: string,
  label: string,
  checkboxes: CheckboxGroupFieldDefinition<TFormData>['checkboxes'],
  renderOrOpts?: ((data: TFormData) => boolean) | BaseElementOptions<TFormData>,
): CheckboxGroupFieldDefinition<TFormData> {
  return {
    ...defaultFormElement,
    name,
    label,
    ...(normalizeRenderOrOpts(renderOrOpts) ?? {}),
    type: 'checkbox-group',
    checkboxes,
  };
}
export const createCheckboxGroupElement = createCheckboxGroupElementFn;

function createTagsElementFn<
  TFormData extends Record<string, any>,
  TFieldValue extends FieldValue<TFormData>,
>(
  name: FieldPath<TFormData>,
  label: string,
  onFilter: (inputStr: string) => Promise<TFieldValue[]> | TFieldValue[],
  options?: BaseElementOptions<TFormData> &
    Pick<
      NonCreatableTagsElementDefinition<TFormData, TFieldValue>,
      | 'InputProps'
      | 'getOptionLabel'
      | 'isOptionEqualToValue'
      | 'renderTags'
      | 'renderOption'
      | 'groupBy'
    >,
): NonCreatableTagsElementDefinition<TFormData, TFieldValue>;
function createTagsElementFn<
  TFormData extends Record<string, any>,
  TFieldValue extends FieldValue<TFormData>,
>(
  name: FieldPath<TFormData>,
  label: string,
  onFilter: (inputStr: string) => Promise<TFieldValue[]> | TFieldValue[],
  onNewTagSelected: (tag: string) => Promise<TFieldValue> | TFieldValue,
  options?: CreatableTagsElementDefinition<TFormData> &
    Pick<
      TagsElementDefinition<TFormData, TFieldValue>,
      | 'InputProps'
      | 'getOptionLabel'
      | 'isOptionEqualToValue'
      | 'renderTags'
      | 'renderOption'
      | 'groupBy'
    >,
): CreatableTagsElementDefinition<TFormData, TFieldValue>;
function createTagsElementFn<
  TFormData extends Record<string, any>,
  TFieldValue extends FieldValue<TFormData>,
>(
  name: FieldPath<TFormData>,
  label: string,
  onFilter: (inputStr: string) => Promise<TFieldValue[]> | TFieldValue[],
  onNewOrOpts?:
    | ((tag: string) => Promise<TFieldValue> | TFieldValue)
    | (BaseElementOptions<TFormData> &
        Pick<
          TagsElementDefinition<TFormData, TFieldValue>,
          | 'InputProps'
          | 'getOptionLabel'
          | 'isOptionEqualToValue'
          | 'renderTags'
          | 'groupBy'
        >),
  options?: BaseElementOptions<TFormData> &
    Pick<
      TagsElementDefinition<TFormData, TFieldValue>,
      'InputProps' | 'getOptionLabel' | 'isOptionEqualToValue' | 'renderTags'
    >,
): TagsElementDefinition<TFormData, TFieldValue> {
  const opts = typeof onNewOrOpts === 'object' ? onNewOrOpts : options;

  const element = {
    ...createBaseElement(name, label, opts),
    onFilter,
    type: 'tags',
  };

  if (typeof onNewOrOpts === 'function') {
    return {
      ...element,
      onNewTagSelected: onNewOrOpts,
    } as CreatableTagsElementDefinition<TFormData, TFieldValue>;
  }

  return element as NonCreatableTagsElementDefinition<TFormData, TFieldValue>;
}

export const createTagsElement = createTagsElementFn;

export const createCategoryElement = <
  TFormData extends Record<string, any> = Record<string, any>,
>(
  name: string,
  label: string,
  render?: (data: TFormData) => boolean,
): CategoryTitleDefinition<TFormData> => ({
  type: 'category-title',
  label,
  name,
  render,
});

type FormDefinitionArgsType<TContext extends Record<string, any> | undefined> =
  {
    t: Translate;
    context: TContext;
  };

export type FormFieldDefinitionType<
  TFormData extends Record<string, any> = Record<string, any>,
> =
  | SelectFieldDefinition<TFormData>
  | TextFieldDefinition<TFormData>
  | NumberFieldDefinition<TFormData>
  | RangeFieldDefinition<TFormData>
  | PhoneFieldDefinition<TFormData>
  | DatePickerDefinition<TFormData>
  | TimeFieldDefinition<TFormData>
  | CheckboxFieldDefinition<TFormData>
  | CheckboxGroupFieldDefinition<TFormData>
  | ColorPickerFieldDefinition<TFormData>
  | CategoryTitleDefinition<TFormData>
  | AddressFieldDefinition<TFormData>
  | OrganisationFieldDefinition<TFormData>
  | TeamFieldDefinition<TFormData>
  | LotFieldDefinition<TFormData>
  | LeadFieldDefinition<TFormData>
  | UserFieldDefinition<TFormData>
  | DictionaryFieldDefinition<TFormData>
  | RichTextFieldDefinition<TFormData>
  | RichEditorSuggestionsFieldDefinition<TFormData>
  | CustomElementDefinition<TFormData>
  | TagsElementDefinition<TFormData>
  | ImageElementDefinition<TFormData>
  | ArrayFieldDefinition<TFormData>
  | GroupFieldDefinition<TFormData>;

export type FormDefinitionType<
  TFormData extends Record<string, any> = Record<string, any>,
  TContext extends Record<string, any> | undefined = undefined,
> = (
  args: FormDefinitionArgsType<TContext>,
) => FormFieldDefinitionType<TFormData>[];

// This type recursively removes internal keys (e.g. starting with "__") from an object.
// It keeps the initial type of every object fields values, and only removes internal keys.
export type NonInternalProps<T> = T extends Array<infer U>
  ? Array<NonInternalProps<U>>
  : T extends Record<string, any>
  ? {
      [K in Exclude<keyof T, `__${string}`>]: NonInternalProps<T[K]>;
    }
  : T extends Date
  ? Date
  : T extends (...args: any[]) => any
  ? T
  : T;

// This function recursively replaces empty strings with null
const makeEmptyNull = <T extends Record<string, Primitive>>(obj: T): T => {
  const newObj = { ...obj }; // shallow copy the object to prevent direct modification
  for (const key in newObj) {
    if (
      typeof newObj[key] === 'object' &&
      !Array.isArray(newObj[key]) &&
      newObj[key] != null
    ) {
      newObj[key] = makeEmptyNull(newObj[key]); // recursive call for nested objects
    } else if (newObj[key] === '') {
      newObj[key] = null as any;
    }
  }
  return newObj;
};

export const removeInternalKeys = <T extends Record<string, any>>(
  obj: T,
): NonInternalProps<T> => {
  const processValue = (value: any): any => {
    if (Array.isArray(value)) {
      return value.map(item =>
        typeof item === 'object' && item != null
          ? removeInternalKeys(item)
          : item,
      );
    }
    if (typeof value === 'object' && value != null) {
      return removeInternalKeys(value);
    }
    return value;
  };

  return Object.fromEntries(
    Object.entries(obj)
      .filter(([key]) => !key.startsWith('__'))
      .map(([key, value]) => [key, processValue(value)]),
  ) as NonInternalProps<T>;
};

export type RaFormOnChange<
  TFormData extends Record<string, any> = Record<string, any>,
> = (
  data: DeepPartial<TFormData>,
  name: FieldPath<TFormData> | undefined,
  type: 'change' | undefined,
  helpers: {
    runValidation: () => boolean;
  } & UseFormReturn<TFormData>,
) => void;

export const isFormSubmitDisabled = (
  disabled: boolean,
  isDirty: boolean,
  allowSubmitNotDirty = false,
) => disabled || (!isDirty && !allowSubmitNotDirty);

export type RaFormProps<
  TFormData extends Record<string, any>,
  TContext extends Record<string, any> | undefined,
  TSubmitResponse extends Promise<any> = Promise<any>,
> = React.PropsWithChildren<
  {
    defaultValues?: DeepPartial<NoInfer<TFormData>>;
    freezeInitialDefaultValues?: boolean;
    valuesOverride?: DeepPartial<NoInfer<TFormData>>;
    readOnly?: boolean;
    onSubmit?: (
      data: NonInternalProps<TFormData>,
      unStrippedData: TFormData,
      event?: React.BaseSyntheticEvent<SubmitEvent>,
    ) => TSubmitResponse;
    onChange?: RaFormOnChange<TFormData>;
    onCancel?: (isFormDirty: boolean) => void;
    validate?: (data: TFormData) => Parameters<UseFormSetError<TFormData>>[];
    onFormDirtyChange?: Dispatch<SetStateAction<boolean>>;
    contentScrollable?: boolean;
    gridProps?: GridProps;
    allowSubmitNotDirty?: boolean;
    collapsibleCategories?: Array<{ name: string; count: number }>;
    customExtractMessagesFromError?: (
      error: Error,
      formDataFields: TFormData,
      t: Translate,
    ) => IFormError<TFormData>[];
  } & (TContext extends undefined
    ? {
        formDefinition: FormDefinitionType<TFormData>;
      }
    : {
        formDefinition: FormDefinitionType<TFormData, NonNullable<TContext>>;
        context: NonNullable<TContext>;
      }) &
    (
      | {
          submitButtonText?: string;
          cancelButtonText?: string;
          actionButtonsSlotProps?: {
            container?: BoxProps & PaperProps;
            submitButtonProps?: LoadingButtonProps;
            cancelButtonProps?: ButtonProps;
            hideCancelButton?: boolean;
          };
          actionButtonsComponent?: never;
        }
      | {
          submitButtonText?: never;
          cancelButtonText?: never;
          actionButtonsSlotProps?: never;
          actionButtonsComponent: React.ReactNode;
        }
    )
>;

export const RaForm = <
  TFormData extends Record<string, any>,
  TContext extends Record<string, any> | undefined = undefined,
  TSubmitResponse extends Promise<any> = Promise<any>,
>(
  props: RaFormProps<TFormData, TContext, TSubmitResponse>,
) => {
  const {
    defaultValues,
    freezeInitialDefaultValues = false,
    valuesOverride,
    readOnly = false,
    onSubmit,
    onCancel,
    onChange,
    submitButtonText,
    cancelButtonText,
    validate,
    onFormDirtyChange,
    actionButtonsComponent,
    actionButtonsSlotProps,
    contentScrollable = false,
    gridProps,
    children,
    allowSubmitNotDirty = false,
    collapsibleCategories = [],
    customExtractMessagesFromError,
  } = props;
  const formMethods = useForm<TFormData>({
    defaultValues,
  });
  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors, isDirty, isSubmitting },
    reset,
    watch,
  } = formMethods;

  // State for collapsible categories
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({});

  const toggleCategory = useCallback((categoryName: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName],
    }));
  }, []);

  // only reset form when defaultValues change
  const prevDefaultValues = useRef<DeepPartial<TFormData> | undefined>(
    undefined,
  );
  const prevValuesOverride = useRef<DeepPartial<TFormData> | undefined>(
    undefined,
  );

  useEffect(() => {
    if (
      freezeInitialDefaultValues === false &&
      defaultValues &&
      prevDefaultValues.current != null &&
      !isDeepEqual(prevDefaultValues.current, defaultValues)
    ) {
      reset(defaultValues);
    }
    prevDefaultValues.current = defaultValues;
  }, [defaultValues, freezeInitialDefaultValues, reset]);

  useEffect(() => {
    if (
      valuesOverride &&
      prevValuesOverride.current != null &&
      !isDeepEqual(prevValuesOverride.current, valuesOverride)
    ) {
      getKeys(valuesOverride).forEach(key => {
        setValue(key as Path<TFormData>, valuesOverride[key], {
          shouldDirty: true,
        });
      });
    }
    prevValuesOverride.current = valuesOverride;
  }, [valuesOverride, setValue]);

  // trigger onChange callback when form data changes
  useEffect(() => {
    if (onChange == null) {
      return;
    }

    const subscription = watch((values, { name, type }) => {
      // We test on "undefined" in addition to "change" to call onChange when the data is changed programatically.
      if (type == null || type === 'change') {
        const runValidation = () => {
          let isValid = true;
          if (validate) {
            const validateErrors = validate(values as TFormData);

            validateErrors.forEach(errorArgs => {
              setError(...errorArgs);
              isValid = false;
            });

            if (isValid) {
              clearErrors();
            }
          }
          return isValid;
        };

        onChange?.(values, name, type, { ...formMethods, runValidation });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, onChange, formMethods, validate, setError, clearErrors]);

  const { t } = useLocale();
  const [displayGenericError, setDisplayGenericError] = useState(false);
  const [snackError, setSnackError] = useState<ErrorLike | null>(null);
  useEffect(() => {
    onFormDirtyChange?.(isDirty);
  }, [isDirty, onFormDirtyChange]);

  const handleSnackClose = (
    _event: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackError(null);
    setDisplayGenericError(false);
  };

  const submitNulledValues = useCallback(
    async (formData: TFormData, event?: React.BaseSyntheticEvent) => {
      if (readOnly) {
        return null;
      }
      const cleanedValues = removeInternalKeys(makeEmptyNull(formData));

      if (validate) {
        const validateErrors = validate(formData);

        if (validateErrors.length > 0) {
          validateErrors.forEach(errorArgs => {
            setError(...errorArgs);
          });

          return null;
        }
      }

      try {
        const result = await onSubmit?.(
          cleanedValues,
          formData,
          event as React.BaseSyntheticEvent<SubmitEvent> | undefined,
        );

        return result;
      } catch (error: any) {
        const errorMessages = [
          ...(customExtractMessagesFromError?.(error, formData, t) ?? []),
          ...extractMessageFromError(error, formData, t),
        ];

        if (
          errorMessages.length === 1 &&
          errorMessages[0].errorMessage === 'defaultError'
        ) {
          if (isErrorLike(error)) {
            setSnackError(error);
          } else {
            setDisplayGenericError(true);
          }
        } else {
          errorMessages.forEach(({ field, errorMessage }) => {
            setError(field, { message: errorMessage });
          });
        }

        return null;
      }
    },
    [readOnly, validate, setError, onSubmit, customExtractMessagesFromError, t],
  );

  const watchedValues = watch();
  const isFieldDisabled = useCallback(
    (field: { disabled?: (data: TFormData) => boolean }) =>
      readOnly || field.disabled?.(watchedValues) === true,
    [readOnly, watchedValues],
  );
  const fieldsDefinition =
    'context' in props
      ? props.formDefinition({
          t,
          context: props.context,
        })
      : props.formDefinition({
          t,
          context: undefined,
        });

  const renderFieldsGroup = (fields: FormFieldDefinitionType<TFormData>[]) => {
    const getInitialCount = (categoryName: string) =>
      collapsibleCategories.find(c => c.name === categoryName)?.count;

    // Build category map for collapsible logic
    const categoryMap: Record<
      string,
      {
        startIndex: number;
        fieldCount: number;
        category: CategoryTitleDefinition<TFormData>;
      }
    > = {};
    let currentCategory: string | null = null;
    let fieldsSinceCategory = 0;

    fields.forEach((field, index) => {
      if (field.type === 'category-title') {
        if (currentCategory) {
          const catData = categoryMap[currentCategory];
          if (catData) {
            catData.fieldCount = fieldsSinceCategory;
          }
        }
        currentCategory = field.name;
        categoryMap[field.name] = {
          startIndex: index + 1,
          fieldCount: 0,
          category: field,
        };
        fieldsSinceCategory = 0;
      } else {
        if (currentCategory) {
          fieldsSinceCategory = fieldsSinceCategory + 1;
        }
      }
    });

    // Update last category
    if (currentCategory) {
      const catData = categoryMap[currentCategory];
      if (catData) {
        catData.fieldCount = fieldsSinceCategory;
      }
    }

    // Track current category and field index for collapsible logic
    let activeCategory: string | null = null;
    let fieldIndexInCategory = 0;

    const renderCategoryToggle = (categoryName: string) => {
      const catData = categoryMap[categoryName];
      const initialCount = getInitialCount(categoryName);
      const hasHiddenFields =
        initialCount !== undefined &&
        catData &&
        catData.fieldCount > initialCount;

      if (!hasHiddenFields) {
        return null;
      }

      const isExpanded = expandedCategories[categoryName] ?? false;

      return (
        <Grid key={`${categoryName}-toggle`} size={12}>
          <Box>
            <Button
              size="small"
              variant="text"
              onClick={() => toggleCategory(categoryName)}
              sx={{
                fontSize: '0.75rem',
                color: 'text.secondary',
              }}
            >
              {isExpanded ? t('Show less') : t('Show more')}
            </Button>
          </Box>
        </Grid>
      );
    };

    return (
      <>
        {
          // Render form fields
          fields.map(field => {
            if (field.render && !field.render(watchedValues)) {
              return null;
            }

            // Collapsible category logic
            if (field.type !== 'category-title' && activeCategory != null) {
              const initialCount = getInitialCount(activeCategory);
              const isExpanded = expandedCategories[activeCategory] ?? false;

              if (
                initialCount !== undefined &&
                !isExpanded &&
                fieldIndexInCategory >= initialCount
              ) {
                fieldIndexInCategory = fieldIndexInCategory + 1;
                return null;
              }
              fieldIndexInCategory = fieldIndexInCategory + 1;
            }

            const helpText =
              typeof field.helpText === 'function'
                ? field.helpText(watchedValues)
                : field.helpText;

            const tooltip =
              typeof field.tooltip === 'function'
                ? field.tooltip(watchedValues)
                : field.tooltip;

            switch (field.type) {
              case 'category-title': {
                const prevCategoryToggle = activeCategory
                  ? renderCategoryToggle(activeCategory)
                  : null;

                activeCategory = field.name;
                fieldIndexInCategory = 0;

                return (
                  <>
                    {prevCategoryToggle}
                    <Grid
                      key={field.name}
                      size={{
                        xs: 12,
                        ...(resolveItemSize(field.gridProps, {
                          includeDefaults: false,
                        }) ?? {}),
                      }}
                      sx={resolveItemSx(field.gridProps)}
                    >
                      <Box
                        sx={{
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          pb: 0.75,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          component="h3"
                          sx={{ fontWeight: 600 }}
                        >
                          {field.label}
                        </Typography>
                      </Box>
                    </Grid>
                  </>
                );
              }
              case 'group':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <Grid container spacing={2} {...gridProps}>
                      {renderFieldsGroup(field.fields)}
                    </Grid>
                  </Grid>
                );
              case 'custom':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    {typeof field.element === 'function'
                      ? field.element(watchedValues)
                      : field.element}
                  </Grid>
                );
              case 'text':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaTextField
                      name={field.name}
                      label={field.label}
                      errors={errors}
                      register={register}
                      required={field.required}
                      type={field.inputType}
                      multiline={field.multiline}
                      minRows={field.rows}
                      disabled={isFieldDisabled(field)}
                      suffix={field.suffix}
                      prefix={field.prefix}
                      helpText={helpText}
                      actions={resolveFieldActions(field.actions, {
                        getValues,
                        reset,
                        setValue,
                      })}
                    />
                  </Grid>
                );
              case 'number':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaNumber
                      name={field.name}
                      label={field.label}
                      required={field.required}
                      min={field.min}
                      max={field.max}
                      control={control}
                      disabled={isFieldDisabled(field)}
                      decimalNumbers={field.decimalNumbers}
                      helpText={helpText}
                      suffix={field.suffix}
                      prefix={field.prefix}
                      disableFormatting={field.disableFormatting}
                    />
                  </Grid>
                );
              case 'range':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaRange
                      control={control}
                      name={field.name}
                      label={field.label}
                      required={field.required}
                      slotProps={field.slotProps}
                    />
                  </Grid>
                );
              case 'phone_number':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaPhoneNumber
                      name={field.name}
                      label={field.label}
                      control={control}
                      required={field.required}
                    />
                  </Grid>
                );
              case 'date':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaDate
                      control={control}
                      name={field.name.toString()}
                      label={field.label}
                      required={field.required}
                      disabled={isFieldDisabled(field)}
                    />
                  </Grid>
                );
              case 'time':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaTime
                      control={control}
                      name={field.name.toString()}
                      label={field.label}
                      required={field.required}
                      disabled={isFieldDisabled(field)}
                    />
                  </Grid>
                );
              case 'checkbox-group': {
                const checkboxes = field.checkboxes.map(
                  ({ disabled, ...checkbox }) => ({
                    ...checkbox,
                    disabled:
                      disabled != null ? disabled(watchedValues) : undefined,
                  }),
                );

                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps, {
                      includeDefaults: false,
                    })}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaCheckboxGroup
                      control={control}
                      label={field.label}
                      checkboxes={checkboxes}
                      formValues={watchedValues}
                      disabled={isFieldDisabled(field)}
                    />
                  </Grid>
                );
              }
              case 'checkbox':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaCheckbox
                      name={field.name.toString()}
                      control={control}
                      label={field.label}
                      useSwitch={field.style === 'switch'}
                      disabled={isFieldDisabled(field)}
                      helpText={helpText}
                      tooltip={tooltip}
                    />
                  </Grid>
                );
              case 'select': {
                const {
                  options,
                  disabled: _disabled,
                  helpText: _helpText,
                  ...rest
                } = field;
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaSelect
                      control={control}
                      options={options(watchedValues)}
                      {...rest}
                      helpText={helpText}
                      disabled={isFieldDisabled(field)}
                    />
                  </Grid>
                );
              }
              case 'address':
                return (
                  <Grid
                    key={`${field.path ?? ''}_address`}
                    size={resolveItemSize(field.gridProps, {
                      includeDefaults: false,
                    })}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaAddressInput
                      id={`${field.path ?? ''}_address`}
                      path={field.path}
                      label={field.label}
                      setValue={setValue}
                      disabled={isFieldDisabled(field)}
                      formValues={
                        field.path != null ? watch(field.path) : watchedValues
                      }
                      requiredFields={field.requiredFields?.(watchedValues)}
                      includeGoogleFields={field.includeGoogleFields}
                      countryRestriction={field.countryRestriction}
                    />
                  </Grid>
                );
              case 'organisation':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaOrganisation
                      errors={errors}
                      name={field.name}
                      label={field.label}
                      getValues={getValues}
                      setValue={setValue}
                    />
                  </Grid>
                );
              case 'team':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaTeam
                      required={field.required}
                      name={field.name}
                      label={field.label}
                      control={control}
                      disabled={isFieldDisabled(field)}
                      multiple={field.multiple}
                    />
                  </Grid>
                );
              case 'lot':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaLot
                      required={field.required}
                      name={field.name}
                      label={field.label}
                      control={control}
                      disabled={isFieldDisabled(field)}
                      filters={field.filters}
                      limitToDevelopment={field.limitToDevelopment}
                    />
                  </Grid>
                );
              case 'lead':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaLead
                      errors={errors}
                      name={field.name}
                      label={field.label}
                      placeholder={field.placeholder}
                      getValues={getValues}
                      setValue={setValue}
                      autoFocus={field.autoFocus}
                      customWhereClause={field.customWhereClause}
                      orderBy={field.orderBy}
                      instantQuery={field.instantQuery}
                    />
                  </Grid>
                );
              case 'color-picker':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaColorPicker
                      label={field.label}
                      control={control}
                      name={field.name}
                    />
                  </Grid>
                );

              case 'user':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaUser
                      required={field.required}
                      name={field.name}
                      label={field.label}
                      control={control}
                      disabled={isFieldDisabled(field)}
                      createUserSelected={field.createUserSelected}
                      filters={field.filters}
                      multiple={field.multiple}
                      autoFocus={field.autoFocus}
                      shortlistTeamId={
                        field.shortlistTeamField != null
                          ? watch(field.shortlistTeamField)
                          : undefined
                      }
                    />
                  </Grid>
                );

              case 'dictionary':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaDictionary
                      required={field.required}
                      name={field.name}
                      label={field.label}
                      control={control}
                      dictionaryType={field.dictionaryType}
                      disabled={isFieldDisabled(field)}
                      multiple={field.multiple}
                      valueField={field.valueField}
                    />
                  </Grid>
                );
              case 'rich-text':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <Controller
                      name={field.name}
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <MarkdownComposer
                          placeholder={field.label}
                          key={field?.resetKey}
                          initialValue={value}
                          readOnly={isFieldDisabled(field)}
                          preserveLineBreaks={field.preserveLineBreaks}
                          format={field.format}
                          minHeight={field.minHeight}
                          maxHeight={field.maxHeight}
                          actions={resolveFieldActions(field.actions, {
                            getValues,
                            reset,
                            setValue,
                          })}
                          onChange={newValue =>
                            onChange(
                              newValue as PathValue<TFormData, Path<TFormData>>,
                            )
                          }
                        />
                      )}
                    />
                  </Grid>
                );
              case 'rich-editor-suggestions':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaRichEditorSuggestions
                      name={field.name}
                      label={field.label}
                      control={control}
                      suggestions={field.suggestions}
                      singleLine={field.singleLine}
                      required={field.required}
                      resetKey={field.resetKey}
                      snippetLotId={field.snippetLotId}
                      snippetDevelopmentId={field.snippetDevelopmentId}
                      onImageUpload={field.onImageUpload}
                      disabled={isFieldDisabled(field)}
                    />
                  </Grid>
                );
              case 'tags':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaTags
                      required={field.required}
                      name={field.name}
                      label={field.label}
                      control={control}
                      disabled={isFieldDisabled(field)}
                      onFilter={field.onFilter}
                      onNewTagSelected={
                        'onNewTagSelected' in field
                          ? field.onNewTagSelected
                          : undefined
                      }
                      renderOption={field.renderOption}
                      renderTags={field.renderTags}
                      getOptionLabel={field.getOptionLabel}
                      isOptionEqualToValue={field.isOptionEqualToValue}
                      InputProps={field.InputProps}
                      groupBy={field.groupBy}
                    />
                  </Grid>
                );
              case 'image':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaImageUpload
                      required={field.required}
                      name={field.name}
                      label={field.label}
                      control={control}
                      onFileUpload={field.onFileUpload}
                      disabled={isFieldDisabled(field)}
                      imageObjectFit={field.imageObjectFit}
                    />
                  </Grid>
                );
              case 'array':
                return (
                  <Grid
                    key={field.name}
                    size={resolveItemSize(field.gridProps)}
                    sx={resolveItemSx(field.gridProps)}
                  >
                    <RaArrayInput
                      required={field.required}
                      name={field.name}
                      label={field.label}
                    />
                  </Grid>
                );
              default:
                return null;
            }
          })
          // End render form fields
        }
      </>
    );
  };

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={handleSubmit(submitNulledValues)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          height: contentScrollable ? '100%' : 'auto',
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            overflow: contentScrollable ? 'auto' : 'visible',
          }}
        >
          <Grid
            container
            spacing={2}
            {...gridProps}
            sx={[
              { padding: 2 },
              ...(Array.isArray(gridProps?.sx)
                ? gridProps.sx
                : gridProps?.sx
                ? [gridProps.sx]
                : []),
            ]}
          >
            {renderFieldsGroup(fieldsDefinition)}
          </Grid>
          {children}
        </Box>
        <MutationErrorSnackbar
          error={snackError}
          onClose={() => setSnackError(null)}
        />
        <Snackbar
          open={displayGenericError}
          autoHideDuration={6000}
          onClose={handleSnackClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackClose}
            severity="error"
            sx={{ width: '100%' }}
          >
            {t('An error occured while submitting your form')}
          </Alert>
        </Snackbar>
        {actionButtonsComponent ?? (
          <div
            style={{
              position: 'sticky',
              bottom: 0,
            }}
          >
            <Box
              component={Paper}
              elevation={3}
              {...actionButtonsSlotProps?.container}
              sx={[
                {
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1,
                  padding: 1,
                  borderRadius: 0,
                },
                ...(Array.isArray(actionButtonsSlotProps?.container?.sx)
                  ? actionButtonsSlotProps.container.sx
                  : actionButtonsSlotProps?.container?.sx
                  ? [actionButtonsSlotProps.container.sx]
                  : []),
              ]}
            >
              {!actionButtonsSlotProps?.hideCancelButton && (
                <Button
                  variant="outlined"
                  color="neutral"
                  disabled={isSubmitting}
                  sx={{
                    borderColor: 'divider',
                    px: 2.5,
                  }}
                  {...actionButtonsSlotProps?.cancelButtonProps}
                  onClick={() => {
                    reset(defaultValues);
                    onCancel?.(isDirty);
                  }}
                >
                  {cancelButtonText ?? t('Cancel')}
                </Button>
              )}
              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                disabled={isFormSubmitDisabled(
                  readOnly,
                  isDirty,
                  allowSubmitNotDirty,
                )}
                loading={isSubmitting}
                loadingPosition="start"
                startIcon={<Save />}
                sx={{
                  px: 2.5,
                  gap: 0.5,
                }}
                {...actionButtonsSlotProps?.submitButtonProps}
              >
                {submitButtonText ?? t('Save')}
              </LoadingButton>
            </Box>
          </div>
        )}
      </form>
    </FormProvider>
  );
};
